import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { buildBreadcrumbs } from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import StructuredData, { getArticleSchema, getWebPageSchema } from '@/components/feature/StructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = useMemo(
    () => (article ? getRelatedArticles(article.slug, article.category, 4) : []),
    [article],
  );

  const breadcrumbs = useMemo(
    () =>
      buildBreadcrumbs(
        `/article/${slug}`,
        article?.category,
        slug,
      ),
    [article, slug],
  );

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 打屁股视频大全`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', article.excerpt);
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) metaKw.setAttribute('content', article.tags.join(','));
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical)
        canonical.setAttribute('href', `https://dapiguvideo.com/article/${slug}`);
    }
  }, [article, slug]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <StructuredData data={getArticleSchema(article)} />
      <StructuredData
        data={getWebPageSchema(
          article.title,
          article.excerpt,
          `https://dapiguvideo.com/article/${slug}`,
        )}
      />

      <Navbar />

      <main className="pt-16">
        {/* Article Header */}
        <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-12 max-w-[1280px] mx-auto">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 text-xs text-background-50/70 mb-3 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
              </div>
              <h1 className="text-xl md:text-3xl font-heading font-bold text-background-50 leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbs} />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <article className="flex-1 max-w-[800px]">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <div
                  className="prose-custom text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="w-full lg:w-[300px] shrink-0">
              <div className="sticky top-24">
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-links-line text-primary-500"></i>
                    相关推荐
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.slug}
                        to={`/article/${related.slug}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-20 h-14 rounded-md object-cover shrink-0"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                            {related.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">{related.date}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* More Related */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                更多推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}